import React, { useContext, useRef, useEffect, useState, Fragment } from "react";
import styled from "styled-components";
import Spinner from '../common/Spinner';
import ChatMessages from "../chats/ChatMessages";
import { getChatById } from "../../actions/chatsAction";
import { SelectedChatHistoryMessageContext } from "./Overview";
import { Button, Empty } from "antd";
import { useOnScreen } from "../../utils/customHooks";

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MessageWrapper = styled.div`
  padding: 20px 0px;
  background: white;
  flex: 1;
  max-height: calc(100vh - 58px - 130px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-scrollbar-thumb {
    background-color: darkgrey;
    outline: 1px solid slategrey;
  }
`;

const HeaderContainer = styled.div`
  padding: 20px 20px 0 20px;
  display: flex;
`;

const NameContainer = styled.p`
  flex: 1;
`;

interface MainProps {
  setChatData: React.Dispatch<React.SetStateAction<any>>;
}

export default function Main({ setChatData }: MainProps) {
  const [clientData, setClientData] = React.useState<any>([]);
  const [messagesStartRef, messagesStartVisible] = useOnScreen({ threshold: 0.5 });
  const [chatPosition, setChatPosition] = useState({ position: 0 });
  const [recentMessages, setRecentMessages] = useState([]);
  const [feedback, setFeedback] = React.useState<any>([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const thumbsRef = useRef<NodeListOf<HTMLElement>>();
  const context = useContext(SelectedChatHistoryMessageContext);
  const [conversation, setConversation] = useState([]);
  const [likeIndex, setLikeIndex] = useState<number>(-1);

  const scrollToNext = () => {
    if (!thumbsRef.current || thumbsRef.current.length === 0) return;
    if (likeIndex < thumbsRef.current.length - 1) {
      thumbsRef.current[likeIndex + 1].scrollIntoView({ behavior: "smooth", block: "nearest" });
      thumbsRef.current[likeIndex + 1].classList.add("activeThumb");
      setTimeout(() => {
        thumbsRef.current[likeIndex + 1].classList.remove("activeThumb");
      }, 2000);
      setLikeIndex(likeIndex + 1);
    }
  };

  const scrollToPrevious = () => {
    if (!thumbsRef.current || thumbsRef.current.length === 0) return;
    if (likeIndex > 0) {
      thumbsRef.current[likeIndex - 1].scrollIntoView({ behavior: "smooth", block: "end" });
      thumbsRef.current[likeIndex - 1].classList.add("activeThumb");
      setTimeout(() => {
        thumbsRef.current[likeIndex - 1] && thumbsRef.current[likeIndex - 1].classList.remove("activeThumb");
      }, 1000);
      setLikeIndex(likeIndex - 1);
    }
  };

  useEffect(() => {
    if (context.selectedChat) {
      setLoading(true);
      getChatById(context.selectedChat)().then((resp) => {
        setChatData(resp);
        setFeedback(resp.feedback);
        setConversation(resp.messages);
        setClientData(resp.clientData);
        const messagesCount = resp.messages.length;
        let position = messagesCount - 50;
        if (position < 0) {
          position = 0;
        }
        setChatPosition({ position });
        setRecentMessages(resp.messages.slice(position));
        setLoading(false);
        setLikeIndex(-1);
        thumbsRef.current = document.querySelectorAll(".thumbsUp");
      });
    }
  }, [context.selectedChat]);

  useEffect(() => {
    if (messagesStartVisible) {
      setChatPosition(prevState => {
        let newPosition = prevState.position - 50;
        if (newPosition < 0) {
          newPosition = 0;
        }
        setRecentMessages(conversation.slice(newPosition))
        const temp = conversation.find((message, index) => index === prevState.position) || ""
        const node = document.getElementById(temp.mId)
        if (node && prevState.position !== 0) {
          node.scrollIntoView();
        }
        return { ...prevState, position: newPosition }
      })
    }

  }, [messagesStartVisible]);

  return (
    <Fragment>
      <Spinner loading={loading} />
      <Container key={context.selectedChat} style={{ filter: loading ? "blur(3px)" : "" }}>
        {clientData && clientData.fromName && (
          <HeaderContainer>
            <NameContainer>
              <strong>{clientData.fromName}</strong>
            </NameContainer>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button type="default" style={{ borderRadius: "6px" }}
                disabled={likeIndex <= 0}
                onClick={scrollToPrevious}>
                Previous
              </Button>
              <Button type="primary" style={{ borderRadius: "6px" }}
                disabled={thumbsRef.current && likeIndex === thumbsRef.current.length - 1}
                onClick={scrollToNext}>
                Next
              </Button>
            </div>
          </HeaderContainer>
        )}
        <MessageWrapper>
          <div ref={messagesStartRef as React.LegacyRef<HTMLDivElement>} />
          {Object.keys(conversation).length !== 0 ? clientData && clientData.fromName && <ChatMessages chatId={context.selectedChat} chats={recentMessages} feedback={feedback} />
            : <div style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "20px" }}>
              <Empty />
            </div>}
        </MessageWrapper>
        <div ref={messagesEndRef} />
      </Container>
    </Fragment>
  );
}
